/**
 * useDropdownAccessibility
 *
 * Adds full keyboard navigation and accessibility support for dropdown and flyout menus.
 *
 * - Supports arrow key navigation (Up, Down, Left, Right), Enter, and Escape
 * - Handles focus movement between menu button, flyout menus, and options
 * - Differentiates logic for DROPDOWN vs FLYOUT types
 * - Automatically cleans up listeners on unmount
 *
 * Designed to support nested/flyout menus at any level of depth.
 */
import { onKeyStroke, useActiveElement, useEventListener } from "@vueuse/core";
import { computed, nextTick } from "vue";
import { focusAdjacentTabbable, focusNextElement, focusPreviousElement, getTabbableElements } from "@/common/helpers/keyboard-navigation";
// A radiogroup is one tab stop of its own, so treating it as a menu strands the buttons after it.
const SELF_TABBING_SELECTOR = [
    "textarea",
    '[contenteditable=""]',
    '[contenteditable="true"]',
    '[contenteditable="plaintext-only"]',
    '[role="radiogroup"]',
    'input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"])'
].join(", ");
export var MenuItemType;
(function (MenuItemType) {
    MenuItemType["FLYOUT"] = "FLYOUT";
    MenuItemType["DROPDOWN"] = "DROPDOWN";
    MenuItemType["DEFAULT"] = "DEFAULT";
})(MenuItemType || (MenuItemType = {}));
export const useDropdownAccessibility = (args) => {
    const { menuItemBtnType, submenu, menuToggleBtn, toggleSubMenu, eventTargetEl, isOpen, menu } = args;
    const activeElement = useActiveElement();
    // Handle both raw HTMLElement or Vue component instance
    const menuEl = computed(() => {
        const raw = menu?.value;
        return raw ? ("$el" in raw ? raw.$el : raw) : null;
    });
    const submenuEl = computed(() => {
        const raw = submenu?.value;
        const element = raw ? ("$el" in raw ? raw.$el : raw) : null;
        // Only enable submenu behavior for flyout menus when focused on flyout buttons
        if (element && menuItemBtnType === MenuItemType.FLYOUT) {
            const isFlyoutButtonFocused = activeElement.value?.classList.contains("rec-dropdown-menu__item--flyout");
            return isFlyoutButtonFocused ? element : null;
        }
        // For other menu types, no submenu behavior needed
        return null;
    });
    const isRadioButton = (element) => {
        return element?.tagName === "INPUT" && element?.getAttribute("type") === "radio";
    };
    // Text fields need arrow keys for caret movement, not menu-item navigation.
    const isTextEntry = (element) => {
        if (!element)
            return false;
        if (element.tagName === "TEXTAREA")
            return true;
        if (element.tagName === "INPUT") {
            const type = element.type;
            return !["radio", "checkbox", "button", "submit", "reset", "range"].includes(type);
        }
        return element.isContentEditable;
    };
    // Search/filter/rename panels wearing a menu role own their own tabbing, so Tab stays native there.
    const ownsItsTabbing = (container) => !!container.querySelector(SELF_TABBING_SELECTOR);
    const handleArrowDown = async (e) => {
        const active = activeElement.value;
        if (isRadioButton(active) || isTextEntry(active)) {
            return;
        }
        e.preventDefault();
        if (menuEl.value) {
            e.stopPropagation();
        }
        // In dropdown: open menu if focused on button, then focus first item
        if (menuItemBtnType === MenuItemType.DROPDOWN) {
            if (!isOpen.value) {
                toggleSubMenu();
                await nextTick();
            }
            // Try getting the first submenu-item and if submenu is closed/not available get the first focusable menu-item
            const [first] = getTabbableElements(submenuEl.value ?? menuEl.value);
            if (isRadioButton(first)) {
                first.checked = true;
                first?.focus();
                return;
            }
            //If not radio button, focus the first item in the submenu
            submenuEl.value && focusNextElement([submenuEl.value], activeElement.value);
        }
        // If already open, just cycle focus inside the menu
        menuEl.value && focusNextElement([menuEl.value], activeElement.value);
    };
    const handleArrowUp = (e) => {
        const active = activeElement.value;
        if (isRadioButton(active) || isTextEntry(active)) {
            return;
        }
        e.preventDefault();
        if (menuEl.value) {
            e.stopPropagation();
        }
        menuEl.value && focusPreviousElement([menuEl.value], activeElement.value);
    };
    // Only relevant for flyout menus — close submenu or re-open from button
    const handleArrowRight = async (e) => {
        if (isTextEntry(activeElement.value))
            return;
        e.preventDefault();
        e.stopPropagation();
        if (menuItemBtnType !== MenuItemType.DROPDOWN) {
            if (!isOpen.value && menuItemBtnType === MenuItemType.FLYOUT) {
                toggleSubMenu();
                await nextTick();
            }
            submenuEl.value && focusNextElement([submenuEl.value], activeElement.value);
        }
    };
    // Only relevant for flyout menus — close submenu or re-open from button
    const handleArrowLeft = async (e) => {
        if (isTextEntry(activeElement.value))
            return;
        e.preventDefault();
        if (menuItemBtnType !== MenuItemType.DROPDOWN) {
            menuToggleBtn.value?.focus();
            if (isOpen.value) {
                e.stopPropagation();
                toggleSubMenu();
            }
        }
    };
    const handleEnterOrSpace = async (e) => {
        if (menuItemBtnType === MenuItemType.DEFAULT)
            return;
        if (e.target !== menuToggleBtn.value && !submenuEl.value)
            return;
        e.stopPropagation();
        e.preventDefault();
        if (!isOpen.value) {
            toggleSubMenu();
            await nextTick();
            // Opening a menu moves focus into it, so arrow keys and Escape have somewhere to work from.
            const target = submenuEl.value ?? menuEl.value;
            target && focusNextElement([target], activeElement.value);
        }
        else {
            toggleSubMenu();
        }
    };
    // A menu is a single tab stop: Tab closes it and resumes the page tab order from the trigger.
    const handleTab = (event) => {
        if (menuItemBtnType !== MenuItemType.DROPDOWN || !isOpen.value)
            return;
        const container = menuEl.value;
        const active = activeElement.value;
        if (!container || !active)
            return;
        if (active !== menuToggleBtn.value && !container.contains(active))
            return;
        if (ownsItsTabbing(container))
            return;
        event.preventDefault();
        event.stopPropagation();
        toggleSubMenu();
        const moved = focusAdjacentTabbable(menuToggleBtn, event.shiftKey ? -1 : 1, container);
        if (!moved)
            menuToggleBtn.value?.focus();
    };
    // Closes only one level of menu if using nested menus
    const handleEscape = async (e) => {
        e.preventDefault();
        const toggleBtn = menuToggleBtn.value;
        toggleBtn?.focus();
        if (isOpen.value) {
            e.stopPropagation();
            toggleSubMenu();
            if (menuItemBtnType === MenuItemType.FLYOUT) {
                await nextTick();
                toggleBtn?.focus();
            }
        }
    };
    const keyMap = {
        ArrowDown: handleArrowDown,
        ArrowUp: handleArrowUp,
        ArrowRight: handleArrowRight,
        ArrowLeft: handleArrowLeft,
        Enter: handleEnterOrSpace,
        Escape: handleEscape,
        Tab: handleTab,
        " ": handleEnterOrSpace
    };
    // A pointer press focuses the nearest focusable ancestor, so its focusout is not an exit.
    let isPointerPressed = false;
    const handlePointerDown = () => {
        isPointerPressed = true;
    };
    const handlePointerRelease = () => {
        isPointerPressed = false;
    };
    // A panel holding its own text entry keeps native tabbing, so nothing else closes it once focus walks out.
    const handleFocusOut = (event) => {
        if (menuItemBtnType !== MenuItemType.FLYOUT || !isOpen.value)
            return;
        if (isPointerPressed)
            return;
        // A null relatedTarget is the window losing focus, which must leave the flyout as it was.
        const next = event.relatedTarget;
        if (!next)
            return;
        if (menuToggleBtn.value?.contains(next) || menuEl.value?.contains(next))
            return;
        toggleSubMenu();
    };
    // Cleanup func to clean up key event listeners by caller
    const stopKeyStroke = onKeyStroke(Object.keys(keyMap), (e) => {
        const handler = keyMap[e.key];
        handler?.(e);
    }, { target: eventTargetEl });
    const stopFocusOut = useEventListener(eventTargetEl, "focusout", handleFocusOut);
    // Capture phase, because a nested `.stop` on a menu container would otherwise swallow the gesture flag.
    const stopPointerDown = useEventListener(eventTargetEl, "pointerdown", handlePointerDown, { capture: true });
    const stopPointerRelease = useEventListener(window, ["pointerup", "pointercancel"], handlePointerRelease);
    return () => {
        stopKeyStroke();
        stopFocusOut();
        stopPointerDown();
        stopPointerRelease();
    };
};
